import React, { useEffect } from 'react'
import { AiOutlineShoppingCart } from 'react-icons/ai'
import { useSelector, useDispatch } from 'react-redux'
import { Link, redirect, useNavigate, useSearchParams } from 'react-router-dom'
import { get_dashboard_index_data } from '../../store/reducers/dashboardReducer'
import { get_guest_orders } from '../../store/reducers/orderReducer'
import { get_orders } from '../../store/reducers/orderReducer'

const Index = ({ sellerId, sellerName }) => {

    const navigate = useNavigate()
    const [searchParams] = useSearchParams()
    const { userInfo } = useSelector(state => state.auth)
    const { totalOrder, pendingOrder, recentOrders, cancelledOrder } = useSelector(state => state.dashboard)
    const { guestCustomer, myOrders } = useSelector(state => state.order)

    const dispatch = useDispatch()

    // Check if we're in guest mode
    const email = searchParams.get('email') || localStorage.getItem('guestEmail')
    const phone = searchParams.get('phone') || localStorage.getItem('guestPhone')
    const isGuestMode = !userInfo && (email || phone)

    useEffect(() => {
        console.log('Index useEffect triggered:', { 
            userInfo, 
            isGuestMode, 
            email, 
            phone
        })
        
        if (userInfo) {
            console.log('Fetching dashboard data for authenticated user:', userInfo.id)
            dispatch(get_dashboard_index_data(userInfo.id))
        } else if (isGuestMode && (email || phone)) {
            console.log('Fetching guest orders for:', { email, phone })
            // Fetch guest orders for dashboard
            dispatch(get_guest_orders({ email, phone }))
        }
    }, [userInfo, isGuestMode, email, phone])

    // Additional effect to fetch orders when they're searched for
    useEffect(() => {
        // Check if we're on the orders page and orders have been fetched
        const isOnOrdersPage = window.location.pathname.includes('/my-orders')
        
        if (isOnOrdersPage && isGuestMode && (email || phone)) {
            console.log('On orders page, fetching guest orders for dashboard counts')
            dispatch(get_guest_orders({ email, phone }))
        } else if (isOnOrdersPage && userInfo) {
            console.log('On orders page, fetching user orders for dashboard counts')
            dispatch(get_orders({ status: 'all', customerId: userInfo.id }))
        }
    }, [window.location.pathname, userInfo, isGuestMode, email, phone])

    // Calculate dashboard stats for guest orders
    const calculateGuestStats = () => {
        console.log('Calculating guest stats with myOrders:', myOrders)
        if (!myOrders || myOrders.length === 0) {
            console.log('No guest orders found')
            return { total: 0, pending: 0, cancelled: 0, recent: [] }
        }

        const total = myOrders.length
        const pending = myOrders.filter(order => order.delivery_status === 'pending' || order.delivery_status === 'placed').length
        const cancelled = myOrders.filter(order => order.delivery_status === 'cancelled').length
        const recent = myOrders.slice(0, 5) // Get last 5 orders

        console.log('Guest stats calculated:', { total, pending, cancelled, recent: recent.length })
        return { total, pending, cancelled, recent }
    }

    // Calculate stats for authenticated users from myOrders (when fetched from Orders component)
    const calculateUserStats = () => {
        if (!myOrders || myOrders.length === 0) {
            return { total: totalOrder, pending: pendingOrder, cancelled: cancelledOrder }
        }

        const total = myOrders.length
        const pending = myOrders.filter(order => order.delivery_status === 'pending' || order.delivery_status === 'placed').length
        const cancelled = myOrders.filter(order => order.delivery_status === 'cancelled').length

        return { total, pending, cancelled }
    }

    const guestStats = calculateGuestStats()
    const userStats = calculateUserStats()
    
    console.log('Current state:', { 
        userInfo, 
        isGuestMode, 
        guestCustomer, 
        myOrders: myOrders?.length || 0,
        guestStats,
        userStats,
        recentOrders: recentOrders?.length || 0
    })

    const redirect = (ord) => {
        let items = 0;
        for (let i = 0; i < ord.length; i++) {
            items = ord.products[i].quantity + items
        }
        navigate(`/${sellerName}/payment`, {
            state: {
                price: ord.price,
                items,
                orderId: ord._id
            }
        })
    }

    const calculateDiscountedPrice = (product) => {
        if (!product) return 0;

        // If discountPrice is available and valid, use it instead of recalculating
        if (product.discountPrice && product.discountPrice > 0) {
            // The stored discountPrice already includes addons from the product details page
            // So we don't need to add addons again
            return product.discountPrice;
        }

        // Fallback to original calculation if no discountPrice is stored
        const { price = 0, discount = 0, bulkPricing = [] } = product;
        const quantity = product.quantity || 1;
        let addonsPrice = 0;
      
        // Calculate addons price
        if (product.addons && product.addons.length > 0) {
            addonsPrice = product.addons.reduce((total, addon) => {
                return total + (addon.price || 0);
            }, 0);
        }
      
        // First check for bulk pricing
        if (bulkPricing && bulkPricing.length > 0) {
            // Filter out invalid bulk prices and sort by quantity
            const validBulkPricing = bulkPricing
                .filter(bp => bp.quantity != null && bp.price != null)
                .sort((a, b) => b.quantity - a.quantity);
      
            // Find applicable bulk price
            const applicableBulkPrice = validBulkPricing.find(
                bp => quantity >= bp.quantity
            );
      
            if (applicableBulkPrice && applicableBulkPrice.price) {
                // For bulk pricing, multiply bulk price by quantity; add-ons scale with quantity
                return (applicableBulkPrice.price * quantity) + (addonsPrice * quantity);
            }
        }
      
        // If no bulk pricing applies, calculate regular price with discount
        const basePrice = discount > 0 ? price - (price * discount / 100) : price;
        return (basePrice * quantity) + (addonsPrice * quantity);
    };

    // If in guest mode, show appropriate interface
    if (isGuestMode) {
        return (
            <div>
                {/* Welcome message */}
                <div className="bg-white p-6 rounded-md mb-5">
                    <div className="text-center">
                        <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <AiOutlineShoppingCart className="text-2xl text-blue-600" />
                        </div>
                        <h2 className="text-2xl font-bold text-gray-900 mb-2">
                            {guestStats.total > 0 ? 'Your Orders' : 'Welcome to Our Shop'}
                        </h2>
                        {guestCustomer ? (
                            <p className="text-gray-600 mb-6">
                                Hello <strong>{guestCustomer.name}</strong>! 
                                {guestStats.total > 0 ? ' Here are your order details.' : ' Ready to start shopping?'}
                            </p>
                        ) : (
                            <p className="text-gray-600 mb-6">
                                {guestStats.total > 0 
                                    ? `Tracking orders for: ${email || phone}`
                                    : 'Browse our products and place your first order!'
                                }
                            </p>
                        )}
                    </div>
                </div>

                {/* Show stats only if there are orders */}
                {guestStats.total > 0 && (
                    <div className="grid grid-cols-3 md:grid-cols-1 gap-5 mb-5">
                        <div className="flex justify-center items-center p-5 bg-white rounded-md gap-5">
                            <div className="bg-green-100 w-[47px] h-[47px] rounded-full flex justify-center items-center text-xl ">
                                <span className="text-xl text-green-800">
                                    <AiOutlineShoppingCart />
                                </span>
                            </div>
                            <div className="flex flex-col justify-start items-start text-slate-600">
                                <h2 className="text-3xl font-bold">{guestStats.total}</h2>
                                <span>Orders</span>
                            </div>
                        </div>
                        <div className="flex justify-center items-center p-5 bg-white rounded-md gap-5">
                            <div className="bg-blue-100 w-[47px] h-[47px] rounded-full flex justify-center items-center text-xl ">
                                <span className="text-xl text-blue-800">
                                    <AiOutlineShoppingCart />
                                </span>
                            </div>
                            <div className="flex flex-col justify-start items-start text-slate-600">
                                <h2 className="text-3xl font-bold">{guestStats.pending}</h2>
                                <span>Pending Orders</span>
                            </div>
                        </div>
                        <div className="flex justify-center items-center p-5 bg-white rounded-md gap-5">
                            <div className="bg-red-100 w-[47px] h-[47px] rounded-full flex justify-center items-center text-xl ">
                                <span className="text-xl text-red-800">
                                    <AiOutlineShoppingCart />
                                </span>
                            </div>
                            <div className="flex flex-col justify-start items-start text-slate-600">
                                <h2 className="text-3xl font-bold">{guestStats.cancelled}</h2>
                                <span>Cancelled Orders</span>
                            </div>
                        </div>
                    </div>
                )}

                {/* My Orders Section */}
                <div className="bg-white p-4 rounded-md">
                    <h2 className="text-lg font-semibold text-slate-600 mb-4">
                        My Orders
                    </h2>
                    {guestStats.total > 0 ? (
                        <div className="text-center py-8">
                            <p className="text-gray-600 mb-4">
                                You have {guestStats.total} order{guestStats.total !== 1 ? 's' : ''} in total.
                            </p>
                            <Link
                                to={`/${sellerName}/dashboard/my-orders`}
                                className="inline-flex items-center px-6 py-3 bg-indigo-600 text-white font-medium rounded-md hover:bg-indigo-700 transition-colors"
                            >
                                <AiOutlineShoppingCart className="mr-2" />
                                View All Orders
                            </Link>
                        </div>
                    ) : (
                        <div className="text-center py-8">
                            <p className="text-gray-600 mb-4">
                                You haven't placed any orders yet.
                            </p>
                            <Link
                                to={`/${sellerName}`}
                                className="inline-flex items-center px-6 py-3 bg-indigo-600 text-white font-medium rounded-md hover:bg-indigo-700 transition-colors"
                            >
                                <AiOutlineShoppingCart className="mr-2" />
                                Start Shopping
                            </Link>
                        </div>
                    )}
                </div>
            </div>
        )
    }

    return (
      <div>
        <div className="grid grid-cols-3 md:grid-cols-1 gap-5">
          <div className="flex justify-center items-center p-5 bg-white rounded-md gap-5">
            <div className="bg-green-100 w-[47px] h-[47px] rounded-full flex justify-center items-center text-xl ">
              <span className="text-xl text-green-800">
                <AiOutlineShoppingCart />
              </span>
            </div>
            <div className="flex flex-col justify-start items-start text-slate-600">
              <h2 className="text-3xl font-bold">{userStats.total}</h2>
              <span>Orders</span>
            </div>
          </div>
          <div className="flex justify-center items-center p-5 bg-white rounded-md gap-5">
            <div className="bg-blue-100 w-[47px] h-[47px] rounded-full flex justify-center items-center text-xl ">
              <span className="text-xl text-blue-800">
                <AiOutlineShoppingCart />
              </span>
            </div>
            <div className="flex flex-col justify-start items-start text-slate-600">
              <h2 className="text-3xl font-bold">{userStats.pending}</h2>
              <span>Pending Orders</span>
            </div>
          </div>
          <div className="flex justify-center items-center p-5 bg-white rounded-md gap-5">
            <div className="bg-red-100 w-[47px] h-[47px] rounded-full flex justify-center items-center text-xl ">
              <span className="text-xl text-red-800">
                <AiOutlineShoppingCart />
              </span>
            </div>
            <div className="flex flex-col justify-start items-start text-slate-600">
              <h2 className="text-3xl font-bold">{userStats.cancelled}</h2>
              <span>Cancelled Orders</span>
            </div>
          </div>
        </div>
      </div>
    );
}

export default Index